// src/utils/axios/axiosConfig.js
import axios from "axios";

// Next.js client env = NEXT_PUBLIC_*, CRA env = REACT_APP_*
const API_ORIGIN = (
  process.env.NEXT_PUBLIC_BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "http://localhost:8000"
).replace(/\/+$/, "");

// Your backend uses /api prefix
const API_BASE_URL = `${API_ORIGIN}/api`;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false, // Bearer token auth (not cookies)
  timeout: 20000,
});

let requestInterceptorId;
let responseInterceptorId;

export const setupInterceptors = ({ getToken, logout } = {}) => {
  // avoid stacking interceptors on hot reloads
  if (requestInterceptorId !== undefined) {
    axiosInstance.interceptors.request.eject(requestInterceptorId);
  }
  if (responseInterceptorId !== undefined) {
    axiosInstance.interceptors.response.eject(responseInterceptorId);
  }

  requestInterceptorId = axiosInstance.interceptors.request.use(
    async (config) => {
      // Tell the API which timezone the user is in, so a date-only filter
      // ("2026-07-31") means THEIR local day, not the server's UTC day.
      config.headers = config.headers ?? {};
      config.headers["X-Timezone-Offset"] = String(new Date().getTimezoneOffset());

      if (typeof getToken === "function") {
        try {
          const token = await getToken();
          if (token) {
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch {
          // ignore token failures
        }
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  responseInterceptorId = axiosInstance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const original = error?.config;
      const status = error?.response?.status;

      // Retry once on 401 with a fresh token
      if (status === 401 && original && !original._retry && typeof getToken === "function") {
        original._retry = true;
        try {
          const freshToken = await getToken({ skipCache: true });
          if (freshToken) {
            original.headers = original.headers ?? {};
            original.headers.Authorization = `Bearer ${freshToken}`;
            return axiosInstance(original);
          }
        } catch {
          // fall through
        }

        if (typeof logout === "function") {
          try {
            await logout();
          } catch {
            // ignore
          }
        }
      }

      return Promise.reject(error);
    }
  );
};

export default axiosInstance;
